// libraries
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { keyBy, merge, values, uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useFormContext } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import { Stack } from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { v4 as uuidv4 } from 'uuid'

// state
import {
  setPurchaseOrderInfo,
  setScrapVehicles,
  resetScrapVehiclesEdited,
  setActiveDialogState,
  setInboundTicketId,
} from 'state/inboundTicket'

// specific components
import ActiveMaterialList from 'components/containers/inbound-tickets/components/ActiveMaterialList'
import TransportationInformation from 'components/containers/inbound-tickets/components/TransportationInformation'
import CustomerInformation from 'components/containers/inbound-tickets/components/CustomerInformation'
import InboundTicketFooter from 'components/containers/inbound-tickets/components/InboundTicketFooter'
import SplitTruckAndMaterialDifferentialBlade from 'components/containers/inbound-tickets/split-weights/SplitTruckAndMaterialDifferentialBlade'
import FreightCostBlade from 'components/containers/freight/freight-cost/FreightCostBlade'

// generic components
import NotesModal, { formatNotes } from 'components/generic/modals/dialog-modal/NotesModal'

// helpers and utils
import {
  transformInboundTicket,
  reverseTransformMaterialRow,
  getMaterialRowWithNetFields,
} from 'services/inboundTickets'
import {
  initialTicketInformation,
  getInitialTotalInformation,
  calculateTotalInfoFields,
  getCustomerId,
  updateDifferentialRow,
  updateChargebackFeesAndPromotionsRows,
  getDateTime,
} from 'components/containers/inbound-tickets/utils'
import {
  CHURN_ZERO_EVENTS as cz,
  IBT_VIEW_OPTIONS,
  TRANSPORTATION_TYPE_OPTIONS,
  ROUNDING_PRECISIONS,
  TICKET_STATUSES,
  IBT_ROW_TYPES,
  FEE_OR_PROMOTION_TYPE,
  BILL_STATUSES,
} from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { dispatchSnackbar } from 'utils/helpers'
import { formatScrapVehicles, formatVehicle } from 'utils/data-manipulation/common'
import { useCalculateFreightCost } from 'utils/custom-hooks/useCalculateFreightCost'
import { useScrapVehicles } from 'utils/custom-hooks/useScrapVehicles'
import useUpdateIndustrialIBTPrices from 'utils/custom-hooks/useUpdateIndustrialIBTPrices'
import useAutosave from 'components/containers/inbound-tickets/helpers/autosave'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { DEFAULT_PDF_WIDTH } from 'utils/constants'
import { calculateFeesAndPromotionsTotal, createFeePromotionGridRows } from 'utils/fee-promotion-calculator'

const InboundTicketIndustrialContainer = ({
  purchaseOrderData,
  inboundTicketNotes,
  inboundTicketNotesRef,
  onSaveNotes,
  isUpdate,
  inboundTicketId,
  inboundTicketData,
  billData,
  supplierRef,
  customerDataRef,
  carrierData,
  createInboundTicket,
  updateInboundTicket,
  setErrorMessage,
  loadInformation,
  setLoadInformation,
  loadInformationRef,
  statusIsPaidOrVoided,
  statusIsVoided,
  freightBillIsPaid,
  isDirty,
  hasEditedMaterials,
  setHasEditedMaterials,
  wrappedUpdateTicketActionsAndStatus,
  isAutosaveEnabled,
  onAddVehicle,
  vehicleData,
  onEditScrapVehicle,
  updateVehicleWithCustomerAndIBT,
  userIsInactive,
  isTransfer,
  pricePrepend,
  carrierPricePrepend,
  hasDataConflict,
  ticketHashRef,
  errorsTareRequired,
  setIsFetchingMaterialPrices,
  isInLockedAccountingPeriodV1,
  enableFePdfs,
  handlePrint,
  purchaseOrderLocationId,
  feesAndPromotionsData,
  checkDoNotBuyViolation,
  onTotalUpdate,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const methods = useFormContext()
  const {
    formState: { errors, isValid },
    reset,
    setValue,
    getValues,
    watch,
  } = methods
  const totalApiRef = useGridApiRef()
  const materialsApiRef = useGridApiRef()

  // state
  const [openNotes, setOpenNotes] = useState(false)
  const [initialMaterials, setInitialMaterials] = useState([])
  const [totalInformation, setTotalInformation] = useState(getInitialTotalInformation({}))
  const [openSplitWeightsBlade, setOpenSplitWeightsBlade] = useState(false)
  const [openFreightCostBlade, setOpenFreightCostBlade] = useState(false)
  const [differentialAmount, setDifferentialAmount] = useState(0)
  const { customers } = useSelector(state => state.customer)
  const { scrapVehiclesRef, newScrapVehicle } = useScrapVehicles({})
  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals, enableAxleWeights, defaultNumberOfAxles } =
    settings?.purchaseSettings?.inboundTickets || {}
  const [gridMaterials, setGridMaterials] = useState([])
  const initialRender = useRef(true)
  const idempotencyKey = useRef(uuidv4())
  const { supplementalPriceSheetIds } = useSelector(state => state.materials)
  const { enableTransferTicketsConsumerWeightsFF, useMinimalObt } = useSelector(
    state => state.featureFlags?.variables || {},
  )
  const [currentTotal, setCurrentTotal] = useState(0)

  const getMaterialRows = useCallback(() => {
    if (materialsApiRef?.current?.getRowModels) {
      return Array.from(
        materialsApiRef.current.getRowModels(),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([_, materialRow]) => getMaterialRowWithNetFields(materialRow),
      ).filter(row => !!row.materialTypeId)
    }
    return []
  }, [materialsApiRef])

  const getMaterials = useCallback(
    () => getMaterialRows().map(row => reverseTransformMaterialRow(row)),
    [materialsApiRef],
  )

  const { isFetching: isFetchingPrices, materialTypesData } = useUpdateIndustrialIBTPrices({
    supplementalPriceSheetIds,
    materialsApiRef,
    isDirty,
  })

  useEffect(() => {
    setIsFetchingMaterialPrices(isFetchingPrices)
  }, [isFetchingPrices])

  const getTruckWeights = useCallback(() => {
    if (totalApiRef?.current?.getRow) {
      const truckTotalRow = totalApiRef.current.getRow('truck-total')
      const { isManualGross, isManualTare, axleWeights } = truckTotalRow || {}
      return {
        gross: {
          amount: parseFloat(truckTotalRow?.gross ?? '0', 10),
          units: defaultYardUm,
        },
        tare: {
          amount: parseFloat(truckTotalRow?.tare ?? '0', 10),
          units: defaultYardUm,
        },
        isManualGross,
        isManualTare,
        axleWeights,
      }
    }
    return {
      gross: {
        amount: 0,
        units: defaultYardUm,
      },
      tare: {
        amount: 0,
        units: defaultYardUm,
      },
      isManualGross: false,
      isManualTare: false,
      axleWeights: [],
    }
  }, [totalApiRef])

  const getTruckImages = useCallback(() => {
    if (totalApiRef?.current?.getRow) {
      const truckTotalRow = totalApiRef.current.getRow('truck-total')
      return {
        grossImages: truckTotalRow?.grossImages ?? [],
        tareImages: truckTotalRow?.tareImages ?? [],
      }
    }
    return {
      grossImages: [],
      tareImages: [],
    }
  }, [totalApiRef])

  const selectedCarrier = watch('transportationInfo.carrierLookup')
  const transportationType = watch('transportationInfo.transportationType')
  const userSet = watch('freightCost.userSet')
  const userChargebackSet = watch('freightCost.userChargebackSet')
  const chargeback = watch('freightCost.chargeBack')
  const customerLocationId = watch('customer.locationId')
  const { priorityFreightCostInfo, isLoading: isLoadingFreightLanesData } = useCalculateFreightCost({
    location: customerDataRef?.current?.locations?.find(location => location.id === customerLocationId),
    transportationType,
    carrierId: selectedCarrier?.id || '',
    effectiveDate: getValues('effectiveDate')?.toISOString(),
    netWeight: loadInformation?.netWeight || 0,
    ticketType: 'IBT',
    contract: null, // Purchase orders don't have freight costs yet
  })

  const calculateDifferential = (loadNetValue = null) => {
    const truckGross = Number(totalApiRef.current?.getRow('truck-total')?.gross) || 0
    const truckTare = Number(totalApiRef.current?.getRow('truck-total')?.tare) || 0
    const truckTotal = truckGross - truckTare
    const loadTotal = totalApiRef.current?.getRow('load-total')?.netWeight || 0
    const differential = truckTotal - loadTotal
    updateDifferentialRow(totalApiRef, truckTotal, loadTotal, chargeback, getValues('feeOrPromotion'), loadNetValue)
    setDifferentialAmount(differential)
  }

  const disableFreightCost = freightBillIsPaid || statusIsVoided() || isInLockedAccountingPeriodV1 || useMinimalObt
  const disableChargeback =
    statusIsPaidOrVoided() ||
    isInLockedAccountingPeriodV1 ||
    useMinimalObt ||
    billData?.status === BILL_STATUSES.INVOICED.id
  /**
   * Apply freight cost logic depending on automatic chargeback and user set values
   */
  useEffect(() => {
    if (!freightBillIsPaid && isDirty) {
      if (userSet === false && !disableFreightCost) {
        setValue('freightCost.cost', priorityFreightCostInfo?.freightCost, { shouldDirty: true })
      }
      if (!statusIsPaidOrVoided() && userChargebackSet === false && !disableChargeback) {
        const chargeBackValue = priorityFreightCostInfo?.freightChargeback ?? 0
        setValue('freightCost.chargeBack', chargeBackValue, { shouldDirty: true })
      }
    }
  }, [isDirty, freightBillIsPaid, priorityFreightCostInfo, disableFreightCost, disableChargeback])

  /**
   * Recalculates the totals grid and updates the load information object.
   * - Aggregates material rows to compute load gross, tare, deductions, net weight, and net value.
   * - Updates the 'load-total' row in the totals grid via totalApiRef.
   * - Recalculates differential between truck weight and load total.
   * - Optionally recalculates and updates fees and promotions rows if not explicitly passed.
   * - Updates local loadInformation state with new totals.
   */
  const calculateTotalInfo = useCallback(
    (matRows = [], feesAndPromotionsTotal) => {
      const materialRows = matRows.length > 0 ? matRows : getMaterialRows()

      const totalInfoFields = calculateTotalInfoFields(materialRows, {
        showWeightDecimals,
      })

      const { loadGross, loadTare, weightDeductions, totalNet, loadNetValue, totalDeductions } = totalInfoFields

      totalApiRef.current.updateRows([
        {
          id: 'load-total',
          rowType: IBT_ROW_TYPES.LOAD_TOTAL,
          code: 'Load Total',
          gross: loadGross,
          tare: loadTare,
          totalDeductions,
          netWeight: totalNet,
          netValue: loadNetValue,
        },
      ])
      calculateDifferential(loadNetValue)
      const chargeback = getValues('freightCost.chargeBack')
      const feeOrPromotionValues = getValues('feeOrPromotion')

      const updatedFeesAndPromotionsTotal =
        feesAndPromotionsTotal || feesAndPromotionsTotal === 0
          ? feesAndPromotionsTotal
          : (() => {
              const result = updateChargebackFeesAndPromotionsRows(
                totalApiRef,
                chargeback,
                feeOrPromotionValues,
                loadNetValue,
              )
              return result.updatedFeesAndPromotionsTotal
            })()

      setLoadInformation({
        ...loadInformation,
        loadWeight: {
          gross: loadGross,
          tare: loadTare,
          deductions: weightDeductions,
          net: totalNet,
        },
        netWeight: totalNet,
        netValue: loadNetValue,
        feesAndPromotionsTotal: updatedFeesAndPromotionsTotal,
      })

      // Pass the current total to the parent container for actions
      // feesAndPromotionsTotal already includes freight chargeback as negative
      const totalWithFeesAndPromotions = loadNetValue + updatedFeesAndPromotionsTotal
      handleTotalUpdate(totalWithFeesAndPromotions)
    },
    [loadInformation, materialsApiRef, totalApiRef],
  )

  useEffect(() => {
    if (!isDirty) return // skip on initial page load
    calculateTotalInfo()
  }, [chargeback])

  const formatData = (data, customerId, ticketId, paymentId, dataHash) => {
    const materialRows = getMaterials()
    const aggregatedValues = materialRows.reduce(
      (aggregate, material) => {
        const { netWeight: materialNetWeight, netValue: materialNetValue } = material?.weightAndPrice || {}
        return {
          netWeight: roundTo(aggregate.netWeight + (materialNetWeight?.amount || 0), ROUNDING_PRECISIONS.WEIGHT),
          netValue: roundTo(aggregate.netValue + (materialNetValue?.amount || 0), ROUNDING_PRECISIONS.COST),
        }
      },
      { netWeight: 0, netValue: 0 },
    )
    const scrapVehicleRows =
      scrapVehiclesRef.current?.map(vehicle => ({
        ...vehicle,
        rowIndex: materialRows?.find(material => material.id === vehicle.rowId)?.rowIndex,
      })) || []

    const { axleWeights, ...truckWeight } = getTruckWeights()
    const dataFormatted = {
      ...data,
      ...loadInformationRef.current,
      ...(inboundTicketId && { id: inboundTicketId }),
      hash: dataHash,
      materials: materialRows,
      effectiveDate: getDateTime(data.effectiveDate, inboundTicketData?.effectiveDate).toISOString(),
      status: {
        ...data.status,
        ...getTruckImages(),
        date: new Date().toISOString(),
      },
      customer: {
        customerCommonIdentifierString: data.customer?.name?.customerCommonIdentifierString,
        ...(customerId && { id: customerId }),
      },
      customerId: customerId || null,
      contactId: data.customer?.contactId || null,
      truckWeight,
      axleWeights,
      scrapVehicles: scrapVehicleRows,
      ticketId,
      paymentId,
      carrierId: data.transportationInfo?.carrierLookup?.id || null,
      transportationInfo: {
        ...data.transportationInfo,
        isPickup: data.transportationInfo?.isPickup || 'false',
        purchaseOrderNumber: parseInt(data.transportationInfo?.purchaseOrderNumber, 10) || null,
        purchaseOrderId: data.transportationInfo?.purchaseOrderId || null,
      },
      taxRate: customerDataRef?.current?.id === supplierRef?.current?.id ? customerDataRef?.current?.taxRate : '0',
      vehicleId: data.customer?.vehicle?.id || null,
      locationId: data.customer?.locationId || null,
      uploadcareGroupId: data.uploadcareGroupId,
      docs: data.docs,
      freightCost: {
        cost: data.freightCost?.cost || 0,
        chargeBack: data.freightCost?.chargeBack || 0,
        userSet: data.freightCost?.userSet || false,
        userChargebackSet: data.freightCost?.userChargebackSet || false,
        id: data.freightCost?.id || undefined,
      },
      netWeight: aggregatedValues.netWeight,
      netValue: roundTo(aggregatedValues.netValue - (data?.freightCost?.chargeBack || 0), ROUNDING_PRECISIONS.COST),
    }

    if (getValues('feeOrPromotion')) {
      const feeOrPromotionFormVals = getValues('feeOrPromotion')
      const feeOrPromotionData = Object.values(feeOrPromotionFormVals).map(row => ({
        ...row,
        settingId: row.id,
        appliedAutomatically: true, // This is always true for now. Will change in the future for V2
      }))
      dataFormatted.feeOrPromotion = feeOrPromotionData
    }
    delete dataFormatted.createdDate
    delete dataFormatted.transportationInfo.hasEditedPurchaseOrder

    return dataFormatted
  }

  const handleSuccess = async ({ resIBT, data, customerId, isNew = false, saveAction }) => {
    reset(data)
    setHasEditedMaterials(false)
    ticketHashRef.current.value = resIBT.hash
    trackEvent({
      eventName: isNew ? cz.CREATE_INBOUND_TICKET : cz.UPDATE_INBOUND_TICKET,
      customFields: { container: 'industrial' },
    })
    if (data.customer?.vehicle?.id) {
      await updateVehicleWithCustomerAndIBT(data.customer?.vehicle, customerId, resIBT.id, resIBT.vehicleId)
    }
    dispatch(resetScrapVehiclesEdited())
    dispatchSnackbar(dispatch, 'success', 'Changes Saved')

    if (saveAction === 'print') {
      const docTitle = `Scale Ticket #${resIBT?.ticketId} | ${TICKET_STATUSES[resIBT?.status?.value?.toString()]?.name}`
      if (enableFePdfs) {
        const paperWidth = localStorage.getItem('paperSize') ?? DEFAULT_PDF_WIDTH
        handlePrint(
          `/core-api/purchases/inbound-tickets/${resIBT?.id}/pdf/IBT_SCALE_TICKET?width=${paperWidth}`,
          docTitle,
        )
      } else {
        dispatch(
          setActiveDialogState({
            title: 'Scale Ticket (Full Page)',
            action: 0,
            dialogTitle: `Scale Ticket #${resIBT?.ticketId} | ${
              TICKET_STATUSES[resIBT?.status?.value?.toString()]?.name
            }`,
          }),
        )
      }
    }

    if (saveAction === 'pay') {
      if (isNew) navigate(`/app/purchases/ticket-library/${resIBT.id}`)
      else navigate(`/app/purchases/ticket-library/${resIBT.id}/payment`)
    }

    if (saveAction === 'invoice') {
      // Set the inboundTicketId in Redux state and navigate to invoice page
      dispatch(setInboundTicketId(resIBT.id))
      if (billData?.invoiceId) {
        navigate(`/app/sales/invoices/${billData.invoiceId}`)
      } else {
        navigate('/app/sales/invoices/new')
      }
    }

    if (!saveAction && isNew) {
      navigate(`/app/purchases/ticket-library/${resIBT.id}`)
    }
  }

  const handleError = ({ error, isNew = false }) => {
    const errorMsg =
      error?.data?.error ||
      error?.error ||
      error?.message ||
      `An error occurred ${isNew ? 'creating' : 'updating'} the ticket.`
    trackEvent({
      eventName: isNew ? cz.CREATE_INBOUND_TICKET : cz.UPDATE_INBOUND_TICKET,
      errorMsg,
      customFields: { container: 'industrial' },
    })
    setErrorMessage(errorMsg)
  }

  const preparedPropIbtData = useMemo(() => {
    if (inboundTicketData && ticketHashRef?.current?.value) {
      const customerId = getCustomerId(supplierRef, inboundTicketData, customers)

      return formatData(
        inboundTicketData,
        customerId,
        inboundTicketData?.ticketId,
        inboundTicketData?.paymentId,
        ticketHashRef?.current?.value,
      )
    }
  }, [inboundTicketData, ticketHashRef?.current?.value])

  const handleFinishedGoodChanged = () => {
    // When the FG is updated, it might change the IBT's hash, which would trigger the concurrent update error. To prevent it, we reset the hash here so that it can be updated in the main ibt container
    if (ticketHashRef?.current) {
      ticketHashRef.current.value = ''
    }
  }

  const onSubmit = async (data, _, saveAction = '') => {
    if (saveAction === 'pay' && errorsTareRequired(getMaterialRows(), IBT_VIEW_OPTIONS.INDUSTRIAL)) return

    const materialRows = Array.from(materialsApiRef.current.getRowModels().values())
    const missingMaterialTypeId = materialRows.some(row => !row.materialTypeId && (!!row.gross || !!row.tare))

    if (missingMaterialTypeId) {
      dispatchSnackbar(dispatch, 'error', 'Some rows do not have a material selected')
      return
    }

    const customerId = getCustomerId(supplierRef, inboundTicketData, customers, data)

    const inboundTicketSave = formatData(
      data,
      customerId,
      inboundTicketData?.ticketId,
      inboundTicketData?.paymentId,
      ticketHashRef?.current?.value,
    )

    if (customerId === customerDataRef.current.id) {
      const contactHasViolation = await checkDoNotBuyViolation(customerDataRef.current?.contacts)
      if (contactHasViolation) {
        dispatchSnackbar(dispatch, 'error', 'Customer is on the Ohio Do Not Buy list. Transaction prohibited.')
        return
      }
    }
    if (isUpdate) {
      await updateInboundTicket({
        ticketId: inboundTicketId,
        billId: inboundTicketData?.billId,
        inboundTicketSave,
      })
        .unwrap()
        .then(resIBT => {
          handleSuccess({ resIBT, data, customerId, saveAction })
        })
        .catch(error => {
          handleError({ error })
        })
    } else {
      // Create new ticket
      // On new tickets, inboundTicketNotesRef.current is null until the first note is added, so we need to check for that
      const newNotes =
        inboundTicketNotesRef.current
          ?.map(note => ({
            name: note.note,
            internal: note.isInternal,
          }))
          ?.filter(note => note.note !== '') ?? []

      await createInboundTicket({
        inboundTicketSave: {
          ...inboundTicketSave,
          notes: newNotes,
          ...{ idempotencyKey: idempotencyKey?.current },
        },
      })
        .unwrap()
        .then(resIBT => {
          handleSuccess({ resIBT, data, customerId, isNew: true, saveAction })
        })
        .catch(error => {
          handleError({ error, isNew: true })
        })
    }
  }

  const { isAutoSaving } = useAutosave(
    {
      saveFunction: methods.handleSubmit(onSubmit),
      trigger:
        isAutosaveEnabled &&
        userIsInactive &&
        (isDirty || hasEditedMaterials) &&
        Object.keys(errors).length === 0 &&
        !!supplierRef?.current?.id,
    },
    [inboundTicketData],
  )

  useEffect(() => {
    if (onSubmit) {
      wrappedUpdateTicketActionsAndStatus(onSubmit, hasDataConflict, isAutoSaving)
    }
  }, [inboundTicketData, billData, isDirty, hasEditedMaterials, errors, isUpdate, isAutoSaving, hasDataConflict])

  // Update actions when total changes (for dynamic PAY/INVOICE button)
  useEffect(() => {
    if (onSubmit && currentTotal !== 0) {
      wrappedUpdateTicketActionsAndStatus(onSubmit, hasDataConflict, isAutoSaving, currentTotal)
    }
  }, [currentTotal])

  let loadNotes = []
  if (inboundTicketNotes) {
    loadNotes = formatNotes(inboundTicketNotes.map(note => ({ ...note, name: note.note })))
  }

  /**
   * On initial load, hydrate the form and grid with ticket data if editing an existing IBT,
   * or initialize default values if creating a new IBT.
   * Skip hydration if the user has already made edits or if required F&P settings are still loading.
   */
  useEffect(() => {
    if (isDirty || hasEditedMaterials) return
    if (!feesAndPromotionsData) return
    if (inboundTicketData) {
      const transformedInboundTicket = transformInboundTicket({
        inboundTicketData,
        carrierData,
      })
      const {
        id,
        scaleType,
        customer,
        effectiveDate,
        numberOfPassengers,
        transportationInfo,
        truckWeight,
        status,
        materials,
        scrapVehicles,
        locationId,
        contactId,
        docs,
        freightCost,
        axleWeights,
        ibtFeesAndPromotions,
        ...transformedLoadInformation
      } = transformedInboundTicket
      reset(
        {
          ...initialTicketInformation,
          id,
          scaleType,
          customer: {
            name: customer,
            locationId,
            contactId,
            buysForbidden: customerDataRef?.current?.buysForbidden ? 'No Buy' : undefined,
          },
          effectiveDate,
          numberOfPassengers,
          transportationInfo,
          truckWeight,
          status,
          materialGrid: {
            'truck-total': {
              gross: String(truckWeight?.gross || 0),
              tare: String(truckWeight?.tare || 0),
              axleWeights,
            },
          },
          freightCost,
        },
        {
          keepErrors: true,
          keepDirty: true,
          keepTouched: true,
          keepIsValid: true,
        },
      )

      setValue('customer.name', customer)
      setValue('freightCost', freightCost)
      setValue('transportationInfo.transportationType', transportationInfo.transportationType)
      setValue('transportationInfo.purchaseOrderId', transportationInfo.purchaseOrderId)
      setValue('transportationInfo.hasEditedPurchaseOrder', false)
      setValue('status.value', status?.value)

      const { formattedScrapVehicles = [], scrapVehiclesAsMaterials = [] } = formatScrapVehicles(scrapVehicles)
      const materialRows = [...materials, ...scrapVehiclesAsMaterials]
        .sort((a, b) => {
          if (!a.rowIndex) return 1
          if (!b.rowIndex) return -1
          return a.rowIndex - b.rowIndex
        })
        .map((material, index) => ({
          ...material,
          rowIndex: index + 1,
          finishedGoodTagNumber: material?.finishedGood?.tag,
          finishedGoodItemCount: material?.finishedGood?.itemCount,
        }))
      const previousMaterials = Array.from(
        materialsApiRef?.current ? materialsApiRef.current.getRowModels().values() : [],
      )?.filter(material => !!material.materialTypeId && material.rowType !== 'scrap-vehicle')
      const previousMaterialsDict = keyBy(previousMaterials, 'id')
      const newMaterialsDict = keyBy(materialRows, 'id')
      const joinedMaterials = values(merge({}, previousMaterialsDict, newMaterialsDict))
      setInitialMaterials(joinedMaterials)
      setGridMaterials(materialRows)
      dispatch(setScrapVehicles(formattedScrapVehicles))
      const totalInfoFields = calculateTotalInfoFields(materialRows, {
        showWeightDecimals,
      })
      const { loadGross, loadTare, totalNet, loadNetValue, totalDeductions } = totalInfoFields

      const newTotalInformation = [
        {
          id: 'load-total',
          rowType: IBT_ROW_TYPES.LOAD_TOTAL,
          code: 'Load Total',
          gross: loadGross,
          tare: loadTare,
          totalDeductions,
          netWeight: totalNet,
          netValue: loadNetValue,
        },
        {
          id: 'truck-total',
          rowType: IBT_ROW_TYPES.TRUCK_TOTAL,
          code: 'Truck Total',
          gross: truckWeight?.gross,
          tare: truckWeight?.tare,
          netWeight: truckWeight?.gross - truckWeight?.tare,
          grossImages: status.grossImages,
          tareImages: status.tareImages,
          scaleButtonClicked: false,
          isManualGross: truckWeight.isManualGross,
          isManualTare: truckWeight.isManualTare,
          axleWeights,
        },
      ]
      if (differentialAmount > 0) {
        newTotalInformation.push({
          id: 'differential',
          rowType: IBT_ROW_TYPES.DIFFERENTIAL,
          code: 'Differential',
          gross: null,
          tare: null,
          netWeight: differentialAmount,
          netValue: null,
          totalDeductions: null,
        })
      }
      if (freightCost?.chargeBack) {
        newTotalInformation.push({
          id: 'freight-chargeback',
          rowType: IBT_ROW_TYPES.FREIGHT_CHARGEBACK,
          code: 'Freight Charge',
          gross: null,
          tare: null,
          totalDeductions: null,
          netWeight: null,
          netValue: freightCost?.chargeBack,
        })
      }
      let feesAndPromotionsTotal = 0
      if (ibtFeesAndPromotions?.length > 0) {
        // Filter out unapplied fees for paid tickets
        const activeFeesAndPromotions = ibtFeesAndPromotions.filter(
          fp => !(status?.value === TICKET_STATUSES.PAID.id && !fp.applied),
        )

        // Calculate total using consolidated utility
        feesAndPromotionsTotal = calculateFeesAndPromotionsTotal(
          activeFeesAndPromotions,
          loadNetValue,
          freightCost?.chargeBack || 0,
        )

        // Create grid rows using consolidated utility
        const feePromotionRows = createFeePromotionGridRows(
          activeFeesAndPromotions,
          loadNetValue,
          freightCost?.chargeBack || 0,
          {
            isPaidTicket: status?.value === TICKET_STATUSES.PAID.id,
            showPaymentMethodSpecificIcon: status?.value === TICKET_STATUSES.PAID.id ? false : true,
          },
        )

        newTotalInformation.push(...feePromotionRows)
        // Hydrate feeOrPromotion form field
        const feesAndPromotionsRows = ibtFeesAndPromotions.map(item => ({
          id: item.settingId,
          name: item.name,
          type: item.type,
          amountType: item.amountType,
          amount: item.amount,
          calculatedValue: item.calculatedValue,
          eligiblePaymentTypes: item.eligiblePaymentTypes,
        }))
        setValue('feeOrPromotion', keyBy(feesAndPromotionsRows, 'id'))
      }
      setTotalInformation(newTotalInformation)

      setLoadInformation({
        ...loadInformation,
        transformedLoadInformation,
        feesAndPromotionsTotal,
      })
      setValue('docs', uniqBy(docs, 'uuid'))

      const { purchaseOrderNumber: poNumber, purchaseOrderId: poId } = transportationInfo
      dispatch(
        setPurchaseOrderInfo({
          purchaseOrderNumber: poNumber?.toString() || '',
          purchaseOrderId: poId || '',
        }),
      )

      calculateTotalInfo(materialRows, feesAndPromotionsTotal)
      if (vehicleData) {
        setValue('customer.vehicle', formatVehicle(vehicleData))
      }
    }
    // NEW IBT CASE
    else if (!isUpdate) {
      setValue('freightCost.userSet', false)
      setValue('freightCost.userChargebackSet', false)
      setValue('transportationInfo.transportationType', TRANSPORTATION_TYPE_OPTIONS[1].id)
      const getInitialTotalInformationPayload = {}
      getInitialTotalInformationPayload.feesAndPromotionsData = feesAndPromotionsData
      const feesAndPromotionsRows = feesAndPromotionsData?.Items?.map(item => ({
        id: item.id,
        name: item.name,
        type: item.type,
        amountType: item.amountType,
        amount: item.amount,
        eligiblePaymentTypes: item.eligiblePaymentTypes,
      }))
      setValue('feeOrPromotion', keyBy(feesAndPromotionsRows, 'id'))

      if (enableAxleWeights) {
        getInitialTotalInformationPayload.defaultNumberOfAxles = defaultNumberOfAxles
      }
      setTotalInformation(getInitialTotalInformation(getInitialTotalInformationPayload))
      const feesAndPromotionsTotal = (feesAndPromotionsData?.Items || []).reduce((aggregate, item) => {
        // Calculate the absolute value and apply correct sign
        if (item.amountType === 'FLAT') {
          const absoluteValue = Math.abs(item.amount)
          const finalValue = item.type === FEE_OR_PROMOTION_TYPE.FEE ? -absoluteValue : absoluteValue
          return roundTo(aggregate + finalValue, ROUNDING_PRECISIONS.COST)
        }
        return aggregate
      }, 0)
      setLoadInformation({
        ...loadInformation,
        feesAndPromotionsTotal,
      })
    }
  }, [inboundTicketData, carrierData, vehicleData, feesAndPromotionsData])

  useEffect(() => {
    if (materialsApiRef?.current?.subscribeEvent) {
      materialsApiRef.current.subscribeEvent('rowsUpdate', () => {
        calculateTotalInfo()
        setGridMaterials(getMaterialRows())
      })
    }
  }, [materialsApiRef])

  useEffect(() => {
    if (initialRender.current) return

    const { rowId, vin, year, color, make, model } = newScrapVehicle

    if (vin) {
      // Check if the row already exists in the table
      const existingRow = materialsApiRef?.current?.getRow(rowId)
      const updatedRow = {
        id: rowId,
        vin,
        rowType: IBT_ROW_TYPES.SCRAP_VEHICLE,
        code: `${year} ${color} ${make} ${model}`.replace(/\s\s+/g, ' ').trim(),
      }
      if (!existingRow) {
        updatedRow.rowIndex = materialsApiRef.current.getRowsCount() + 1
      }
      materialsApiRef?.current?.updateRows([updatedRow])
      setHasEditedMaterials(true)
    }
  }, [newScrapVehicle])

  const onSaveNotesLocal = notes => {
    setOpenNotes(false)
    onSaveNotes(notes.map(n => ({ ...n, note: n.name })))
  }

  const onUpdateMaterial = useCallback(() => {
    setHasEditedMaterials(true)
    calculateTotalInfo()
  })

  useEffect(() => {
    initialRender.current = false
  }, [])

  const onUploadFiles = useCallback(uploadUUID => {
    setValue('uploadcareGroupId', uploadUUID)
    onSubmit(getValues())
  }, [])

  const isUploadFilesDisabled = () => {
    if (isUpdate) return !isValid
    return true
  }

  const handleRemoveFiles = files => {
    setValue('docs', files)
    onSubmit({ ...getValues(), docs: files })
  }

  const onSaveDifferential = materials => {
    materialsApiRef?.current?.updateRows(materials)
    onUpdateMaterial()
  }

  const formPurchaseOrderId = watch('transportationInfo.purchaseOrderId')

  useEffect(() => {
    if (!isDirty) return // skip on initial page load
    if (!materialsApiRef?.current?.getRowModels) return // skip if getRowModels function doesn't exist
    if (formPurchaseOrderId && formPurchaseOrderId !== purchaseOrderData?.id) return // skip if still fetching new purchaseOrderData

    const currentRows = Array.from(materialsApiRef.current.getRowModels().values())
    const updatedRows = currentRows.map(row => {
      // use the first entry from the new purchase order that matches the material's materialTypeId
      const purchaseOrderEntry = formPurchaseOrderId
        ? purchaseOrderData?.priceSheet?.entriesObj?.find(entry => entry.materialTypeId === row.materialTypeId)
        : null
      return {
        ...row,
        orderEntryId: purchaseOrderEntry?.id || null,
      }
    })
    materialsApiRef.current.updateRows(updatedRows)
  }, [materialsApiRef, formPurchaseOrderId, purchaseOrderData])

  const handleTotalUpdate = total => {
    if (currentTotal !== total) {
      setCurrentTotal(total)
      if (onTotalUpdate) {
        onTotalUpdate(total)
      }
    }
  }

  return (
    <>
      <SplitTruckAndMaterialDifferentialBlade
        differentialAmount={differentialAmount}
        openBlade={openSplitWeightsBlade}
        setOpenBlade={setOpenSplitWeightsBlade}
        materials={gridMaterials}
        onSave={onSaveDifferential}
        viewMode={IBT_VIEW_OPTIONS.INDUSTRIAL}
        purchaseOrderData={purchaseOrderData}
      />
      <FreightCostBlade
        open={openFreightCostBlade}
        setOpen={setOpenFreightCostBlade}
        getValues={getValues}
        setValue={setValue}
        ticketStatus={inboundTicketData?.status?.value}
        netWeight={loadInformation?.netWeight || 0}
        location={customerDataRef?.current?.locations?.find(location => location.id === customerLocationId)}
        type="IBT"
        pricePrepend={pricePrepend}
        freightBillData={inboundTicketData?.freightCost?.freightBill || null}
        isInLockedAccountingPeriodV1={isInLockedAccountingPeriodV1}
        disableChargeback={disableChargeback}
      />
      <Stack minHeight="calc(100vh - 235px)">
        <Stack direction={{ xs: 'column', md: 'row' }} p={3} paddingBottom={0} spacing={3}>
          <CustomerInformation
            disabled={isTransfer || statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
            inboundTicketLocationId={inboundTicketData?.locationId}
            purchaseOrderLocationId={purchaseOrderLocationId}
            onAddVehicle={onAddVehicle}
            vehicleData={vehicleData}
            isLoading={isUpdate && !inboundTicketData}
            isDirty={isDirty}
          />
          <TransportationInformation
            statusIsPaidOrVoided={statusIsPaidOrVoided}
            disabled={
              (enableTransferTicketsConsumerWeightsFF ? false : isTransfer) ||
              statusIsVoided() ||
              hasDataConflict ||
              isInLockedAccountingPeriodV1
            }
            freightBillIsPaid={freightBillIsPaid}
            ibtData={preparedPropIbtData}
            dispatchTicketData={inboundTicketData?.linkedDispatchTicket}
            materialTypesData={materialTypesData}
            formatData={formatData}
            isLoading={isUpdate && !inboundTicketData}
          />
        </Stack>
        <ActiveMaterialList
          materials={initialMaterials}
          totalInformation={totalInformation}
          onUpdateMaterial={onUpdateMaterial}
          onEditScrapVehicle={onEditScrapVehicle}
          isUpdate={isUpdate}
          apiRef={materialsApiRef}
          totalApiRef={totalApiRef}
          formApi={methods}
          disabled={statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
          setDifferential={setDifferentialAmount}
          onClickDifferentialRow={() => setOpenSplitWeightsBlade(true)}
          hasEditedMaterials={hasEditedMaterials}
          setHasEditedMaterials={setHasEditedMaterials}
          pricePrepend={pricePrepend}
          setErrorMessage={setErrorMessage}
          purchaseOrderData={purchaseOrderData}
          inboundTicketData={inboundTicketData}
          saveIbt={methods.handleSubmit(onSubmit)}
          handleFinishedGoodCreated={handleFinishedGoodChanged}
          handleFinishedGoodDeleted={handleFinishedGoodChanged}
          isTransfer={isTransfer}
          isDirty={isDirty}
          onFeeOrPromotionUpdateSubmit={calculateTotalInfo}
        />
      </Stack>
      <InboundTicketFooter
        loadInformation={loadInformation}
        onClickNotes={() => setOpenNotes(true)}
        notes={loadNotes}
        files={uniqBy(inboundTicketData?.docs || [], 'uuid')}
        onUploadFiles={onUploadFiles}
        onRemoveFiles={handleRemoveFiles}
        disabledUploadFiles={isUploadFilesDisabled() || hasDataConflict || isInLockedAccountingPeriodV1}
        setOpenFreightCostBlade={setOpenFreightCostBlade}
        isLoadingFreightLanesData={isLoadingFreightLanesData}
        pricePrepend={pricePrepend}
        carrierPricePrepend={carrierPricePrepend}
      />
      <NotesModal
        notes={loadNotes}
        open={openNotes}
        onSave={onSaveNotesLocal}
        onClose={() => setOpenNotes(false)}
        disabled={hasDataConflict}
      />
    </>
  )
}

InboundTicketIndustrialContainer.propTypes = {
  isUpdate: PropTypes.bool,
  inboundTicketId: PropTypes.string,
  inboundTicketData: PropTypes.object,
  billData: PropTypes.object,
  supplierRef: PropTypes.object,
  customerDataRef: PropTypes.object,
  carrierData: PropTypes.object,
  createInboundTicket: PropTypes.func.isRequired,
  updateInboundTicket: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func,
  loadInformation: PropTypes.object,
  setLoadInformation: PropTypes.func,
  loadInformationRef: PropTypes.object,
  statusIsPaidOrVoided: PropTypes.func,
  freightBillIsPaid: PropTypes.bool,
  isDirty: PropTypes.bool,
  hasEditedMaterials: PropTypes.bool,
  setHasEditedMaterials: PropTypes.func,
  wrappedUpdateTicketActionsAndStatus: PropTypes.func,
  isAutosaveEnabled: PropTypes.bool,
  onAddVehicle: PropTypes.func.isRequired,
  vehicleData: PropTypes.object,
  updateVehicleWithCustomerAndIBT: PropTypes.func.isRequired,
  userIsInactive: PropTypes.bool,
  onEditScrapVehicle: PropTypes.func.isRequired,
  isTransfer: PropTypes.bool,
  pricePrepend: PropTypes.string,
  carrierPricePrepend: PropTypes.string,
  purchaseOrderData: PropTypes.object,
  errorsTareRequired: PropTypes.func,
  setIsFetchingMaterialPrices: PropTypes.func,
  enableFePdfs: PropTypes.bool,
  handlePrint: PropTypes.func,
  purchaseOrderLocationId: PropTypes.string,
  feesAndPromotionsData: PropTypes.object,
  checkDoNotBuyViolation: PropTypes.func,
  onTotalUpdate: PropTypes.func,
}

InboundTicketIndustrialContainer.defaultProps = {
  isUpdate: false,
  inboundTicketId: null,
  inboundTicketData: null,
  billData: {},
  supplierRef: null,
  customerDataRef: null,
  carrierData: null,
  setErrorMessage: () => {},
  loadInformation: {},
  setLoadInformation: () => {},
  loadInformationRef: {},
  statusIsPaidOrVoided: () => {},
  freightBillIsPaid: false,
  isDirty: false,
  hasEditedMaterials: false,
  setHasEditedMaterials: () => {},
  wrappedUpdateTicketActionsAndStatus: () => {},
  isAutosaveEnabled: false,
  vehicleData: null,
  userIsInactive: false,
  isTransfer: false,
  pricePrepend: '$',
  carrierPricePrepend: '$',
  purchaseOrderData: null,
  errorsTareRequired: () => {},
  setIsFetchingMaterialPrices: () => {},
  enableFePdfs: false,
  handlePrint: () => {},
  purchaseOrderLocationId: null,
  feesAndPromotionsData: null,
  checkDoNotBuyViolation: () => {},
  onTotalUpdate: () => {},
}

export default InboundTicketIndustrialContainer
